import { ManifestComplianceEscalation, SubmissionStatus } from "generated-graphql/graphql";

export type EManifestComplianceSeverity = "error" | "warning" | "none";

export interface EManifestComplianceDisplay {
  tooltipText: string;
  severity: EManifestComplianceSeverity;
  /**
   * True when the customer has taken the required compliance action
   * (contacted the transporter at 45 days, started/filed an exception report at 60 days).
   * Callers can use this to dim icons or otherwise soften the visual treatment.
   */
  actionTaken: boolean;
}

/**
 * Narrow input shape covering only the fields the display logic needs.
 * Lets the function be unit-tested without constructing a full GraphQL row.
 */
export interface EManifestComplianceInput {
  eManifestComplianceEscalation?: ManifestComplianceEscalation | null;
  exceptionReportSubmission?: { status?: SubmissionStatus | null } | null;
  transporterContactedAt?: string | Date | null;
}

/**
 * Single source of truth for the compliance tooltip/severity shown by the
 * "e-Manifest" icon column and the "Days Awaiting e-Manifest" number column
 * on the waste manifest list.
 *
 * Keeping both columns routed through this function prevents drift — e.g.
 * one column saying "contact the transporter" after the transporter has
 * already been contacted, or a checkmark suggesting "resolved" while a
 * dimmed error icon means "handled."
 */
export function getEManifestComplianceDisplay(
  row: EManifestComplianceInput
): EManifestComplianceDisplay {
  const escalation = row.eManifestComplianceEscalation;
  const submission = row.exceptionReportSubmission;

  if (escalation === ManifestComplianceEscalation.Overdue_60) {
    if (submission?.status === SubmissionStatus.FilingComplete) {
      return {
        tooltipText: "Exception report filed",
        severity: "error",
        actionTaken: true,
      };
    }
    if (submission) {
      return {
        tooltipText: "Exception report started",
        severity: "error",
        actionTaken: true,
      };
    }
    return {
      tooltipText: "60+ days since shipment — an exception report is required",
      severity: "error",
      actionTaken: false,
    };
  }

  if (escalation === ManifestComplianceEscalation.Overdue_45) {
    if (row.transporterContactedAt) {
      return {
        tooltipText: "Transporter contacted",
        severity: "warning",
        actionTaken: true,
      };
    }
    return {
      tooltipText: "45+ days since shipment — contact the transporter",
      severity: "warning",
      actionTaken: false,
    };
  }

  return {
    tooltipText: "Awaiting an e-Manifest record. Will escalate at 45 and 60 days.",
    severity: "none",
    actionTaken: false,
  };
}
